import React, { ReactElement, ReactNode } from 'react'

import cn from 'classnames'
import type * as CSS from 'csstype'

import buttonConfiguration, { buttonValidation } from './buttonConfig'
import { CoreButtonWrapper } from './coreButton.style'

interface IConfig {
  buttonClass: string
  collapsedClass: string
  loadButtonClass: string
}

interface IConfigurationOptions {
  variation: buttonValidation
}

const classButtonConfig = ({ variation }: IConfigurationOptions): IConfig => {
  return buttonConfiguration[variation] as IConfig
}

interface IPrimaryButtonProps {
  text?: string | ReactNode
  variation?: buttonValidation
  disabled?: boolean
  onClick?: (e?: any) => void | Promise<void>
  fullWidth?: boolean
  isLoading?: boolean
  width?: string
  style?: CSS.Properties
  className?: string
  type?: 'button' | 'submit' | 'reset'
  tooltip?: string
  ['data-test']?: string
  collapsed?: boolean
  CollapsedIcon?: ReactElement
}

interface ILoadingButton {
  loadButtonClass: string
  variation: buttonValidation
  fullWidth?: boolean
  width?: string
}

const LoadingButton = ({ loadButtonClass, variation, fullWidth, width, ...props }: ILoadingButton) => {
  return (
    <CoreButtonWrapper style={{ width: fullWidth ? '100%' : width }} className={loadButtonClass} {...props}>
      <div className='loading-button'>
        <span style={{ margin: '7px' }} className='dot1'></span>
        <span style={{ margin: '7px' }} className='dot2'></span>
        <span style={{ margin: '7px' }} className='dot3'></span>
        <span style={{ margin: '7px' }} className='dot4'></span>
      </div>
    </CoreButtonWrapper>
  )
}

export const CoreButton = ({
  text,
  disabled,
  onClick,
  isLoading,
  variation = 'primary',
  width,
  style,
  className,
  tooltip,
  type = 'button',
  fullWidth = false,
  collapsed = false,
  CollapsedIcon,
  ...props
}: IPrimaryButtonProps) => {
  const config: IConfig = classButtonConfig({ variation, ...props })

  if (isLoading) {
    return (
      <LoadingButton
        fullWidth={fullWidth}
        width={width}
        loadButtonClass={config.loadButtonClass}
        variation={variation}
        {...props}
      />
    )
  }

  return !collapsed ? (
    <CoreButtonWrapper
      data-test='core-button-base'
      onClick={onClick}
      style={{ ...style, width: fullWidth ? '100%' : width }}
      disabled={disabled}
      {...props}
      className={cn(className, config.buttonClass)}>
      {text && (
        <>
          {React.isValidElement(text) ? (
            text
          ) : (
            <span data-test={`core-button-text-${text}`} className='body-medium'>
              {text}
            </span>
          )}
        </>
      )}
    </CoreButtonWrapper>
  ) : (
    <CoreButtonWrapper
      data-test='core-button-base'
      onClick={onClick}
      style={{ ...style }}
      disabled={disabled}
      type={type}
      data-tip={tooltip}
      className={cn(className, config.collapsedClass)}>
      {CollapsedIcon || <span className='icon'>+</span>}
    </CoreButtonWrapper>
  )
}
